import { useEffect, useRef } from "react";
import { usePrefersReducedMotion } from "~/hooks/usePrefersReducedMotion";
import { isFiniteNumber } from "~/utils/numeric";

/**
 * Scrolls the selected element (matching selector) into view within a horizontal
 * scroll container, centering it when possible. Runs when `selectedKey` changes
 * and when the container resizes (e.g. window resize).
 *
 * Edge cases: unmounted container, detached nodes, no overflow, already at target,
 * invalid selector, scrollTo unsupported, NaN/Infinity, prefers-reduced-motion.
 *
 * @param selectedKey - Value that triggers scroll when it changes (e.g. tab slug)
 * @param selector - CSS selector for the selected element (default: '[data-selected]')
 */
export function useScrollSelectedIntoView(
  selectedKey: string | undefined,
  selector = "[data-selected]",
) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const didScrollRef = useRef(false);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    didScrollRef.current = false;

    const scrollIntoView = (forceScroll = false) => {
      if (didScrollRef.current && !forceScroll) return;
      if (typeof window !== "undefined" && window.matchMedia("print").matches)
        return; // Skip during print

      const container = scrollContainerRef.current;
      if (!container) return;
      if (!container.isConnected) return; // Detached from DOM

      let selected: HTMLElement | null = null;
      try {
        selected = container.querySelector<HTMLElement>(selector);
      } catch {
        return; // Invalid selector
      }
      if (!selected || !selected.isConnected) return;

      const containerRect = container.getBoundingClientRect();
      if (!isFiniteNumber(containerRect.width) || containerRect.width <= 0) {
        return; // Container not visible (e.g. display:none)
      }

      const maxScroll = container.scrollWidth - container.clientWidth;
      if (!isFiniteNumber(maxScroll) || maxScroll <= 0) return; // No overflow

      const selectedRect = selected.getBoundingClientRect();
      const selectedLeftInContent =
        container.scrollLeft + (selectedRect.left - containerRect.left);
      const scrollTarget =
        selectedLeftInContent -
        containerRect.width / 2 +
        selectedRect.width / 2;

      if (!isFiniteNumber(scrollTarget)) return;

      const clampedTarget = Math.max(0, Math.min(scrollTarget, maxScroll));

      if (Math.abs(clampedTarget - container.scrollLeft) < 1) return; // Already at target

      didScrollRef.current = true;

      const behavior = prefersReducedMotion ? "auto" : "smooth";

      try {
        if (typeof container.scrollTo === "function") {
          container.scrollTo({ left: clampedTarget, behavior });
        } else {
          container.scrollLeft = clampedTarget;
        }
      } catch {
        // Fallback for environments where scrollTo options throw (e.g. JSDOM, old browsers)
        container.scrollLeft = clampedTarget;
      }
    };

    // Wait for DOM update (e.g. Headless UI setting data-selected)
    const raf = requestAnimationFrame(() =>
      requestAnimationFrame(() => scrollIntoView()),
    );
    const fallback = setTimeout(() => scrollIntoView(), 150);

    // Re-scroll when container resizes so selected stays in view
    const container = scrollContainerRef.current;
    let resizeRafId: number | null = null;
    const resizeObserver =
      container && typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => {
            if (resizeRafId != null) cancelAnimationFrame(resizeRafId);
            resizeRafId = requestAnimationFrame(() => {
              resizeRafId = null;
              scrollIntoView(true);
            });
          })
        : null;
    if (container) resizeObserver?.observe(container);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(fallback);
      if (resizeRafId != null) cancelAnimationFrame(resizeRafId);
      resizeObserver?.disconnect();
    };
  }, [selectedKey, selector, prefersReducedMotion]);

  return scrollContainerRef;
}
